import React from "react";
import { motion } from "framer-motion";
import {
    CircuitBoard,
    Mail,
    Phone,
    MapPin,
    Linkedin,
    Twitter,
    Instagram,
    Youtube,
    ArrowUpRight,
    Send,
} from "lucide-react";
import { company, products, services, navLinks } from "@/data/landing";

const socials = [
    { Icon: Linkedin, href: "#", label: "LinkedIn" },
    { Icon: Twitter, href: "#", label: "Twitter" },
    { Icon: Instagram, href: "#", label: "Instagram" },
    { Icon: Youtube, href: "#", label: "YouTube" },
];

const Footer = () => {
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-black border-t border-white/5 overflow-hidden"
        >
            {/* Gradient accent line */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />

            {/* Decorative orbs */}
            <motion.div
                className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(0,229,255,0.10), transparent 60%)",
                    filter: "blur(60px)",
                }}
                animate={{ scale: [1, 1.15, 1], y: [0, 20, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(124,92,255,0.10), transparent 60%)",
                    filter: "blur(60px)",
                }}
                animate={{ scale: [1, 1.2, 1], y: [0, -20, 0] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                {/* HERO LINE */}
                <div className="pt-24 pb-16 text-center border-b border-white/5">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300"
                    >
                        <span className="h-px w-8 bg-cyan-400/50" />
                        From Prayagraj · For India
                        <span className="h-px w-8 bg-cyan-400/50" />
                    </motion.span>
                    <h2
                        data-testid="footer-tagline"
                        className="mt-6 font-display font-bold text-white text-5xl md:text-7xl lg:text-[6rem] leading-[0.95] tracking-tight"
                    >
                        <span className="text-grad-cyan">Atvicon</span>
                        <br className="md:hidden" />
                        <span className="text-zinc-500"> · </span>
                        <span className="italic font-light text-white/80">
                            Technologies
                        </span>
                    </h2>
                    <p className="mt-6 text-zinc-400 text-lg max-w-xl mx-auto">
                        Software, shipped with conviction — from Colonel Ganj, Prayagraj
                        to the world.
                    </p>
                </div>

                {/* MAIN GRID */}
                <div className="py-16 grid md:grid-cols-12 gap-12">
                    {/* About + contact */}
                    <div className="md:col-span-5 lg:col-span-4">
                        <div className="flex items-center gap-2.5">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400/30 to-violet-500/30 border border-white/10">
                                <CircuitBoard className="h-5 w-5 text-cyan-300" />
                            </span>
                            <div className="flex flex-col leading-none">
                                <span className="font-display font-bold text-white text-base">
                                    Atvicon
                                </span>
                                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                                    Technologies
                                </span>
                            </div>
                        </div>
                        <p className="mt-5 text-zinc-400 text-sm leading-relaxed">
                            A product & engineering studio building AI-first software for
                            Indian aspirants, advocates, and ambitious businesses.
                        </p>

                        <div className="mt-7 space-y-3">
                            <a
                                href={`tel:${company.phone.replace(/\s/g, "")}`}
                                data-testid="footer-phone"
                                className="group flex items-center gap-3 text-zinc-400 hover:text-white transition"
                            >
                                <span className="h-9 w-9 rounded-lg bg-white/[0.04] border border-white/10 grid place-items-center group-hover:bg-cyan-400/10 group-hover:border-cyan-400/30 transition">
                                    <Phone className="h-4 w-4 text-cyan-300" strokeWidth={1.75} />
                                </span>
                                <span className="font-mono text-sm tracking-wide">
                                    {company.phone}
                                </span>
                            </a>
                            <a
                                href={`mailto:${company.email}`}
                                data-testid="footer-email"
                                className="group flex items-center gap-3 text-zinc-400 hover:text-white transition"
                            >
                                <span className="h-9 w-9 rounded-lg bg-white/[0.04] border border-white/10 grid place-items-center group-hover:bg-cyan-400/10 group-hover:border-cyan-400/30 transition">
                                    <Mail className="h-4 w-4 text-cyan-300" strokeWidth={1.75} />
                                </span>
                                <span className="text-sm">{company.email}</span>
                            </a>
                            <div className="group flex items-start gap-3 text-zinc-400">
                                <span className="h-9 w-9 rounded-lg bg-white/[0.04] border border-white/10 grid place-items-center shrink-0">
                                    <MapPin className="h-4 w-4 text-cyan-300" strokeWidth={1.75} />
                                </span>
                                <span className="text-sm leading-relaxed">
                                    {company.address}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Links columns */}
                    <div className="md:col-span-7 lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 gap-8">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300">
                                Company
                            </div>
                            <ul className="mt-5 space-y-3 text-sm">
                                {navLinks.map((l) => (
                                    <li key={l.href}>
                                        <a
                                            href={l.href}
                                            className="text-zinc-400 hover:text-white transition flex items-center gap-1 group"
                                        >
                                            {l.label}
                                            <ArrowUpRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition" />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300">
                                Products
                            </div>
                            <ul className="mt-5 space-y-3 text-sm">
                                {products.map((p) => (
                                    <li key={p.id}>
                                        <a
                                            href={p.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-zinc-400 hover:text-white transition flex items-center gap-1 group"
                                        >
                                            {p.name}
                                            <ArrowUpRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition" />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300">
                                Services
                            </div>
                            <ul className="mt-5 space-y-3 text-sm">
                                {services.map((s) => (
                                    <li key={s.id}>
                                        <a
                                            href="#services"
                                            className="text-zinc-400 hover:text-white transition"
                                        >
                                            {s.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Newsletter / CTA */}
                    <div className="md:col-span-12 lg:col-span-3">
                        <div className="relative glass rounded-2xl p-6 overflow-hidden">
                            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-cyan-400/20 blur-2xl pointer-events-none" />
                            <div className="relative">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300">
                                    Stay in the loop
                                </div>
                                <h3 className="mt-3 font-display font-semibold text-white text-lg leading-tight">
                                    Quarterly drop of product updates & build notes.
                                </h3>
                                <form
                                    onSubmit={(e) => e.preventDefault()}
                                    className="mt-5 flex items-center gap-2 rounded-lg bg-white/[0.04] border border-white/10 p-1 focus-within:border-cyan-400/40"
                                >
                                    <input
                                        type="email"
                                        placeholder="you@email.com"
                                        data-testid="footer-newsletter-input"
                                        className="flex-1 bg-transparent text-sm text-white placeholder:text-zinc-600 px-3 py-2 outline-none"
                                    />
                                    <button
                                        type="submit"
                                        data-testid="footer-newsletter-submit"
                                        aria-label="Subscribe"
                                        className="h-9 w-9 grid place-items-center rounded-md bg-cyan-400 hover:bg-cyan-300 text-black transition"
                                    >
                                        <Send className="h-4 w-4" />
                                    </button>
                                </form>
                                <p className="mt-3 text-[11px] text-zinc-500">
                                    No spam. Unsubscribe anytime.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="border-t border-white/5 py-7 flex flex-col md:flex-row items-center justify-between gap-4">
                    <p className="text-xs text-zinc-500 font-mono">
                        © {new Date().getFullYear()} Atvicon Technologies. All rights
                        reserved.
                    </p>
                    <div className="flex items-center gap-2">
                        {socials.map(({ Icon, href, label }, i) => (
                            <a
                                key={i}
                                href={href}
                                aria-label={label}
                                data-testid={`footer-social-${i}`}
                                className="h-9 w-9 grid place-items-center rounded-md border border-white/10 text-zinc-400 hover:text-cyan-300 hover:border-cyan-400/40 hover:bg-white/[0.04] transition"
                            >
                                <Icon className="h-4 w-4" strokeWidth={1.75} />
                            </a>
                        ))}
                    </div>
                    <p className="text-xs text-zinc-500 font-mono flex items-center gap-2">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
                        Crafted in Prayagraj · Engineered for India
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
